import React, { useLayoutEffect, useRef } from 'react';
import OrderDesign from '../OrderDesign';
import {
  MOBILE_HAND_CARD_BASE_HEIGHT,
  MOBILE_HAND_CARD_BASE_WIDTH,
} from '../components/mobile/mobileHandCarouselLayout';
import './TerfaShadowSniperFlyOverlay.css';

const START_FRAC = 0.12;
const FLIGHT_MS = 720;

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2;
}

function scaleAlongArc(progress) {
  const peak = Math.sin(Math.PI * progress);
  return START_FRAC + (1 - START_FRAC) * peak;
}

function getBattlefieldSlotCenterPixels(zone, index) {
  if (typeof document === 'undefined' || !zone || index == null) {
    return null;
  }
  const slotElement = document.querySelector(
    `[data-slot-zone="${zone}"][data-slot-index="${index}"]`
  );
  if (!slotElement) {
    return null;
  }
  const rect = slotElement.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return null;
  }
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

function getPlayerProfileImageCenterPixels() {
  if (typeof document === 'undefined') {
    return null;
  }
  const profileImg =
    document.querySelector('img.player-hud-avatar[data-zone="player_general"]')
    || document.querySelector('[data-zone="player_general"]');
  if (!profileImg) {
    return null;
  }
  const rect = profileImg.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return null;
  }
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

/**
 * Shadow Sniper flies Ter'fa → player profile: tiny at source, full size mid-flight, tiny on profile.
 */
export const TerfaShadowSniperFlyOverlay = ({
  grant,
  order,
  cardScale = 1,
  mobileOuterScale = 1,
  isMobileHand = false,
  gameState = null,
  userId = null,
  onComplete,
}) => {
  const overlayRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const flightCompletedRef = useRef(false);

  useLayoutEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const grantKey = grant?.key ?? '';
  const grantZone = grant?.zone ?? '';
  const grantIndex = grant?.index;

  useLayoutEffect(() => {
    flightCompletedRef.current = false;

    if (!grantZone || grantIndex == null || !order) {
      return undefined;
    }

    const root = overlayRef.current;
    if (!root) {
      return undefined;
    }

    let cancelled = false;
    let rafId = 0;
    let startTime = null;
    let cachedStart = null;
    let measureAttempts = 0;
    let warmupFrames = 0;

    const completeOnce = () => {
      if (flightCompletedRef.current || cancelled) {
        return;
      }
      flightCompletedRef.current = true;
      onCompleteRef.current?.();
    };

    root.style.opacity = '0';
    root.style.pointerEvents = 'none';

    const tick = (now) => {
      if (cancelled) {
        return;
      }

      if (warmupFrames < 2) {
        warmupFrames += 1;
        rafId = requestAnimationFrame(tick);
        return;
      }

      if (!cachedStart) {
        cachedStart = getBattlefieldSlotCenterPixels(grantZone, grantIndex);
      }
      const targetCenter = getPlayerProfileImageCenterPixels();

      if (!cachedStart || !targetCenter) {
        measureAttempts += 1;
        if (measureAttempts < 28) {
          rafId = requestAnimationFrame(tick);
          return;
        }
        completeOnce();
        return;
      }

      if (startTime === null) {
        startTime = now;
      }

      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / FLIGHT_MS);
      const e = easeInOutCubic(t);
      const x = cachedStart.x + (targetCenter.x - cachedStart.x) * e;
      const y = cachedStart.y + (targetCenter.y - cachedStart.y) * e;
      const scale = scaleAlongArc(t);

      root.style.position = 'fixed';
      root.style.left = `${x}px`;
      root.style.top = `${y}px`;
      root.style.transform = `translate(-50%, -50%) scale(${scale})`;
      root.style.transformOrigin = 'center center';
      root.style.opacity = t < 0.05 ? String(t / 0.05) : '1';
      root.style.zIndex = '100002';

      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        completeOnce();
      }
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [grantKey, grantZone, grantIndex, isMobileHand]);

  if (!grant || !order) {
    return null;
  }

  const orderDesign = (
    <OrderDesign
      order={order}
      count={0}
      minimal
      scale={cardScale}
      isOOR={!!order.isOOR}
      isPlayable={order.isPlayable}
      gameState={gameState}
      userId={userId}
      imageDraggable={false}
      lazyLoad
    />
  );

  return (
    <div ref={overlayRef} className="terfa-shadow-sniper-fly-overlay" aria-hidden="true">
      {isMobileHand ? (
        <div
          className="terfa-shadow-sniper-fly-overlay__mobile-shell"
          style={{
            width: `${MOBILE_HAND_CARD_BASE_WIDTH}px`,
            height: `${MOBILE_HAND_CARD_BASE_HEIGHT}px`,
            transform: `scale(${mobileOuterScale})`,
            transformOrigin: 'bottom left',
          }}
        >
          <div className="terfa-shadow-sniper-fly-overlay__mobile-inner">
            {orderDesign}
          </div>
        </div>
      ) : (
        <div className="terfa-shadow-sniper-fly-overlay__desktop-shell">
          <div className="terfa-shadow-sniper-fly-overlay__desktop-inner">
            <div className="terfa-shadow-sniper-fly-overlay__desktop-order-wrap">
              {orderDesign}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
